import {
  collection,
  doc,
  getDocs,
  QueryDocumentSnapshot,
  setDoc,
  updateDoc
} from "firebase/firestore";
import { GripVertical, Plus, Trash2 } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/ui/page-header";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
// dnd-kit imports
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { db } from "../../api/firebaseApi";
import { VISAVALUE } from "../../types/visa.type";

type NormalizedChecklistItem =
  | { kind: "item"; title: string }
  | { kind: "group"; title: string; items: NormalizedChecklistItem[] }
  | { kind: "note"; title: string };

// Local editable item type with stable IDs for drag-and-drop
type EditableChecklistItem =
  | { id: string; kind: "item"; title: string }
  | { id: string; kind: "group"; title: string; items: EditableChecklistItem[] }
  | { id: string; kind: "note"; title: string };

const generateId = (): string =>
  `id_${Math.random().toString(36).slice(2)}_${Date.now()}`;

const toEditable = (
  items: NormalizedChecklistItem[]
): EditableChecklistItem[] => {
  return items.map((it) => {
    if (it.kind === "group") {
      return {
        id: generateId(),
        kind: "group",
        title: it.title,
        items: toEditable(it.items)
      };
    }
    return {
      id: generateId(),
      kind: it.kind,
      title: it.title
    } as EditableChecklistItem;
  });
};

type DocumentsChecklistDoc = {
  visaType: string;
  items: NormalizedChecklistItem[];
};

type ChecklistListEditorProps = {
  items: EditableChecklistItem[];
  onChange: (items: EditableChecklistItem[]) => void;
  depth: number; // 0..2 (3 levels total)
};

const GROUP_LABELS = ["Group", "Sub-Group", "Section"];

type DragHandleProps = Pick<
  ReturnType<typeof useSortable>,
  "attributes" | "listeners"
>;

const DragHandle = ({ attributes, listeners }: DragHandleProps) => (
  <Button
    type="button"
    variant="ghost"
    size="icon-sm"
    aria-label="drag"
    className="cursor-grab"
    {...attributes}
    {...listeners}
  >
    <GripVertical />
  </Button>
);

const DeleteButton = ({
  label,
  onClick
}: {
  label: string;
  onClick: () => void;
}) => (
  <Button
    type="button"
    variant="danger"
    size="icon-sm"
    aria-label={label}
    onClick={onClick}
  >
    <Trash2 />
  </Button>
);

type ItemRowProps = {
  it: EditableChecklistItem;
  idx: number;
  depth: number;
  allowAddGroup: boolean;
  updateAt: (index: number, next: EditableChecklistItem) => void;
  removeAt: (index: number) => void;
};

const ItemRow = ({
  it,
  idx,
  depth,
  allowAddGroup,
  updateAt,
  removeAt
}: ItemRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: it.id });
  const fieldId = useId();
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.9 : 1
  } as React.CSSProperties;

  // Top-level rows are the outlined cards; nested rows sit on a fill inside
  // their group so cards never nest.
  const shell =
    depth === 0
      ? "rounded-lg bg-elevated ring-1 ring-line-2"
      : "rounded-md bg-fill-1";

  if (it.kind === "item") {
    return (
      <div ref={setNodeRef} style={style} className={cn("p-3", shell)}>
        <div className="flex items-start gap-3">
          <DragHandle attributes={attributes} listeners={listeners} />
          <div className="flex flex-1 flex-col gap-1.5">
            <Label htmlFor={fieldId}>Item</Label>
            <Input
              id={fieldId}
              placeholder="Title"
              value={it.title}
              onChange={(e) =>
                updateAt(idx, {
                  id: it.id,
                  kind: "item",
                  title: e.target.value
                })
              }
            />
          </div>
          <DeleteButton label="Delete item" onClick={() => removeAt(idx)} />
        </div>
      </div>
    );
  }
  if (it.kind === "note") {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className={cn(
          "bg-warn-soft p-3",
          depth === 0 ? "rounded-lg" : "rounded-md"
        )}
      >
        <div className="flex items-start gap-3">
          <DragHandle attributes={attributes} listeners={listeners} />
          <div className="flex flex-1 flex-col gap-1.5">
            <Label htmlFor={fieldId}>Note</Label>
            <Textarea
              id={fieldId}
              placeholder="Write a short note shown before the following items/groups"
              value={it.title}
              onChange={(e) =>
                updateAt(idx, {
                  id: it.id,
                  kind: "note",
                  title: e.target.value
                })
              }
              rows={2}
            />
          </div>
          <DeleteButton label="Delete note" onClick={() => removeAt(idx)} />
        </div>
      </div>
    );
  }
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn("flex flex-col gap-3 p-3", shell)}
    >
      <div className="flex items-start gap-3">
        <DragHandle attributes={attributes} listeners={listeners} />
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor={fieldId}>
            {GROUP_LABELS[depth] ?? GROUP_LABELS[GROUP_LABELS.length - 1]}
          </Label>
          <Input
            id={fieldId}
            placeholder="Group name"
            value={it.title}
            onChange={(e) => updateAt(idx, { ...it, title: e.target.value })}
          />
        </div>
        <DeleteButton label="Delete group" onClick={() => removeAt(idx)} />
      </div>
      <div className="ml-3.5 flex flex-col gap-2 border-l border-line-2 pl-3">
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={() =>
              updateAt(idx, {
                ...it,
                items: [
                  ...it.items,
                  { id: generateId(), kind: "item", title: "" }
                ]
              })
            }
          >
            <Plus />
            Add Item
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={() =>
              updateAt(idx, {
                ...it,
                items: [
                  ...it.items,
                  { id: generateId(), kind: "note", title: "" }
                ]
              })
            }
          >
            <Plus />
            Add Note
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={() =>
              allowAddGroup &&
              updateAt(idx, {
                ...it,
                items: [
                  ...it.items,
                  { id: generateId(), kind: "group", title: "", items: [] }
                ]
              })
            }
            disabled={!allowAddGroup}
          >
            <Plus />
            Add Group
          </Button>
        </div>
        {it.items.length ? (
          <ChecklistListEditor
            items={it.items}
            onChange={(children: EditableChecklistItem[]) =>
              updateAt(idx, { ...it, items: children })
            }
            depth={depth + 1}
          />
        ) : (
          <p className="text-sm text-fg-3">No items</p>
        )}
      </div>
    </div>
  );
};

function ChecklistListEditor({
  items,
  onChange,
  depth
}: ChecklistListEditorProps) {
  const addItem = () => {
    onChange([...items, { id: generateId(), kind: "item", title: "" }]);
  };

  const addGroup = () => {
    onChange([
      ...items,
      { id: generateId(), kind: "group", title: "", items: [] }
    ]);
  };

  const updateAt = (index: number, next: EditableChecklistItem) => {
    onChange(items.map((it, i) => (i === index ? next : it)));
  };

  const removeAt = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onChange(arrayMove(items, oldIndex, newIndex));
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const allowAddGroup = depth < 2; // max depth = 2 (0,1,2)

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <SortableContext
        items={items.map((i) => i.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="flex flex-col gap-3">
          {items.map((it, idx) => (
            <ItemRow
              key={it.id}
              it={it}
              idx={idx}
              depth={depth}
              allowAddGroup={allowAddGroup}
              updateAt={updateAt}
              removeAt={removeAt}
            />
          ))}
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" onClick={addItem}>
              <Plus />
              Add Item
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={addGroup}
              disabled={!allowAddGroup}
            >
              <Plus />
              Add Group
            </Button>
          </div>
        </div>
      </SortableContext>
    </DndContext>
  );
}

const ALL_VISAS: string[] = Object.values(VISAVALUE).filter(
  (v) => v !== VISAVALUE.EMPTY
);

export const DocumentsChecklist = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedVisa, setSelectedVisa] = useState<string>(ALL_VISAS[0]);
  const [existing, setExisting] = useState<
    Record<string, DocumentsChecklistDoc>
  >({});
  const [draftItems, setDraftItems] = useState<EditableChecklistItem[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const migrateLegacy = (
    items: any[] | undefined
  ): NormalizedChecklistItem[] => {
    if (!Array.isArray(items)) return [];
    const result: NormalizedChecklistItem[] = [];
    for (const raw of items) {
      if (raw) {
        if (typeof raw === "string") {
          result.push({ kind: "item", title: raw });
        } else if (raw.kind === "item") {
          result.push({ kind: "item", title: (raw.title ?? "") as string });
        } else if (raw.kind === "note") {
          result.push({ kind: "note", title: (raw.title ?? "") as string });
        } else if (raw.kind === "group") {
          const child = migrateLegacy(raw.items as any[]);
          result.push({
            kind: "group",
            title: (raw.title ?? "") as string,
            items: child
          });
        } else {
          // Fallback: coerce unknown shapes to items
          try {
            result.push({ kind: "item", title: String(raw) });
          } catch (_) {
            // ignore
          }
        }
      }
    }
    return result;
  };

  const loadExisting = async () => {
    const snaps = await getDocs(collection(db, "documents_checklist"));
    const map: Record<string, DocumentsChecklistDoc> = {};
    snaps.forEach((d: QueryDocumentSnapshot) => {
      const data = d.data() as DocumentsChecklistDoc;
      map[d.id] = { ...data };
    });
    setExisting(map);
  };

  // Sync draft when visa selection or data changes
  useEffect(() => {
    const items = existing[selectedVisa]?.items ?? [];
    const migrated = migrateLegacy(JSON.parse(JSON.stringify(items)) as any[]);
    setDraftItems(toEditable(migrated));
  }, [existing, selectedVisa]);

  useEffect(() => {
    (async () => {
      await loadExisting();
      setIsLoading(false);
    })();
  }, []);

  const addSingleItem = () => {
    setDraftItems((prev) => [
      ...prev,
      { id: generateId(), kind: "item", title: "" }
    ]);
  };

  const addGroup = () => {
    setDraftItems((prev) => [
      ...prev,
      { id: generateId(), kind: "group", title: "", items: [] }
    ]);
  };

  const addNote = () => {
    setDraftItems((prev) => [
      ...prev,
      { id: generateId(), kind: "note", title: "" }
    ]);
  };

  const sanitize = (
    items: EditableChecklistItem[]
  ): NormalizedChecklistItem[] => {
    const output: NormalizedChecklistItem[] = [];
    for (const it of items) {
      if (it.kind === "item") {
        const title = (it.title ?? "").trim();
        if (title) output.push({ kind: "item", title });
      } else if (it.kind === "note") {
        const title = (it.title ?? "").trim();
        if (title) output.push({ kind: "note", title });
      } else {
        const title = (it.title ?? "").trim();
        const children = sanitize(Array.isArray(it.items) ? it.items : []);
        if (title && children.length) {
          output.push({ kind: "group", title, items: children });
        }
      }
    }
    return output;
  };

  const saveDraft = async () => {
    setIsSaving(true);
    try {
      const ref = doc(db, "documents_checklist", selectedVisa);
      const items = sanitize(draftItems);
      await updateDoc(ref, { items });
      await loadExisting();
      toast.success("Checklist saved");
    } catch (e) {
      // If doc missing (should not, but for safety), create it
      try {
        const ref = doc(db, "documents_checklist", selectedVisa);
        const items = sanitize(draftItems);
        await setDoc(ref, { visaType: selectedVisa, items });
        await loadExisting();
        toast.success("Checklist saved");
      } catch (err) {
        toast.error("Save failed");
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div
        className="flex min-h-50 w-full items-center justify-center gap-2 bg-surface text-base text-fg-3"
        aria-busy
      >
        <Spinner />
        Loading checklist
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-surface">
      <div className="flex flex-col gap-4 px-6 pt-6 pb-3">
        <PageHeader
          title="Documents Checklist"
          description="Manage visa-specific document checklists."
          actions={
            <>
              <Select value={selectedVisa} onValueChange={setSelectedVisa}>
                <SelectTrigger aria-label="Visa Type" className="w-48">
                  <SelectValue placeholder="Visa Type" />
                </SelectTrigger>
                <SelectContent>
                  {ALL_VISAS.map((v) => (
                    <SelectItem key={v} value={v}>
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={saveDraft}
                disabled={isSaving}
              >
                {isSaving ? <Spinner /> : null}
                Save Changes
              </Button>
            </>
          }
        />
        <div className="flex flex-wrap items-center gap-2">
          <span className="mr-2 text-base font-medium text-fg">
            {selectedVisa}
          </span>
          <Button type="button" size="sm" onClick={addSingleItem}>
            <Plus />
            Add Item
          </Button>
          <Button type="button" size="sm" onClick={addNote}>
            <Plus />
            Add Note
          </Button>
          <Button type="button" size="sm" onClick={addGroup}>
            <Plus />
            Add Group
          </Button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-24">
        <div className="flex max-w-240 flex-col gap-3">
          {draftItems.length === 0 && (
            <p className="text-base text-fg-3">No items</p>
          )}
          <ChecklistListEditor
            items={draftItems}
            onChange={setDraftItems}
            depth={0}
          />
        </div>
      </div>
    </div>
  );
};
